import { Flex, Text, useToken } from '@tradener/lumen';
import { DateTime } from 'luxon';
import { CartesianGrid, LabelList, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { CreditSeriesPointProps } from '@graphql/query/operationLimits/credit/counterpartDetail';

import CounterpartyChartYAxisUnitLabel from '../CounterpartyChartYAxisUnitLabel';
import {
  buildYearReferenceLines,
  formatMillionCurrencyLabel,
  formatMtmTick,
  shouldRenderValueLabel,
  toMillionValue,
} from '../chartFormatters';

export interface CounterpartyMtmChartProps {
  series: CreditSeriesPointProps[];
  activeCompetence?: string;
  onHoverCompetence: (competence?: string) => void;
  shouldAnimate?: boolean;
  hideHeader?: boolean;
}

const COLORS = {
  gray100: 'var(--lumen-colors-gray-100)',
  gray500: 'var(--lumen-colors-gray-500)',
  gray900: 'var(--lumen-colors-gray-900)',
  orange500: 'var(--lumen-colors-orange-500)',
  orange700: 'var(--lumen-colors-orange-700)',
};

const Y_AXIS_WIDTH = 92;
const CHART_HEIGHT = '30rem';
const SYNC_ID = 'operationLimitsDashboardSync';

function ChartTooltip({ active, payload, label }: any) {
  const [gray0] = useToken('colors', ['gray.0']);

  if (!active || !payload?.length) return null;

  return (
    <Flex bgColor={gray0} py="2" px="3" flexDirection="column" borderRadius="lg" boxShadow="lg" gap="2">
      <Text color="gray.900" textStyle="body2-medium">
        {DateTime.fromISO(label).setLocale('pt-BR').toFormat('MMM yyyy')}
      </Text>
      {payload.map(({ payload: point, name, color }: any) => (
        <Flex key={name} alignItems="center" justifyContent="space-between" gap="5">
          <Flex alignItems="center" gap="2">
            <Flex width="3.5" height=".125rem" bg={color} borderRadius="full" />
            <Text color="gray.500" textStyle="caption1-regular">
              {name}
            </Text>
          </Flex>
          <Text color="gray.900" textStyle="caption1-medium">
            {Number(point?.value ?? 0).formatCurrency('pt-BR')}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

function CounterpartyMtmChart({
  series,
  onHoverCompetence,
  shouldAnimate = false,
  hideHeader = false,
}: CounterpartyMtmChartProps) {
  const [gray900] = useToken('colors', ['gray.900']);
  const chartData = series.map((point) => ({
    ...point,
    displayValue: toMillionValue(point.value),
  }));
  const hasNonZeroValues = series.some((point) => Number(point.value) !== 0);
  const yearReferenceLines = buildYearReferenceLines(series.map(({ competence }) => competence));

  if (!series.length) {
    return (
      <Flex
        data-testid="mtm-chart"
        flexDirection="column"
        bgColor="gray.0"
        height={CHART_HEIGHT}
        width="full"
        borderRadius="xl"
        padding="5"
        color={COLORS.gray900}
      >
        <Flex
          flexDirection="column"
          justifyContent="center"
          gap="0.5"
          visibility={hideHeader ? 'hidden' : 'visible'}
          aria-hidden={hideHeader}
        >
          <Text textStyle="subheading1-semibold">MtM por competência</Text>
        </Flex>
        <Flex flex="1" align="center" justify="center">
          <Text textStyle="body2-regular" color="gray.500">
            -
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      data-testid="mtm-chart"
      flexDirection="column"
      bgColor="gray.0"
      height={CHART_HEIGHT}
      width="full"
      borderRadius="xl"
      padding="5"
      color={COLORS.gray900}
    >
      <Flex
        flexDirection="column"
        justifyContent="center"
        gap="0.5"
        visibility={hideHeader ? 'hidden' : 'visible'}
        aria-hidden={hideHeader}
      >
        <Text textStyle="subheading1-semibold">MtM por competência</Text>
      </Flex>
      <Flex
        direction="column"
        width="full"
        flex="1"
        minHeight="0"
        paddingBlock="1"
        paddingInline="3"
        paddingLeft="0"
        sx={{
          '.recharts-text': { fill: COLORS.gray500 },
          '.recharts-cartesian-axis-line': { stroke: 'none' },
          '.recharts-cartesian-axis-tick-line, line': { stroke: COLORS.gray100 },
        }}
        overflow="visible"
      >
        <ResponsiveContainer>
          <LineChart
            data={chartData}
            margin={{ top: 35, right: 24, bottom: 0, left: 0 }}
            syncId={SYNC_ID}
            onMouseMove={(state) => onHoverCompetence(state?.activeLabel)}
            onMouseLeave={() => onHoverCompetence(undefined)}
          >
            <CartesianGrid vertical={false} strokeWidth={1} />
            <XAxis
              dataKey="competence"
              tickFormatter={(value) => DateTime.fromISO(value).toFormat('MM/yy')}
              tickLine={false}
              axisLine={false}
              interval={0}
              angle={-55}
              textAnchor="end"
              height={68}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tickFormatter={formatMtmTick}
              width={Y_AXIS_WIDTH}
              tickMargin={18}
              tickLine={false}
              axisLine={false}
              padding={{ top: 35 }}
              label={<CounterpartyChartYAxisUnitLabel value="R$ (milhões)" fill={COLORS.gray500} />}
            />
            {yearReferenceLines.map(({ year, competence }) => (
              <ReferenceLine
                key={`${year}-${competence}`}
                x={competence}
                stroke={`${gray900}29`}
                strokeWidth={2}
                ifOverflow="discard"
                isFront={false}
                strokeDasharray="6 6"
                label={{ value: year, position: 'insideTopLeft', fill: COLORS.gray500 }}
              />
            ))}
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: `${gray900}14`, fill: `${gray900}14`, radius: 2 }} />
            <Line
              type="monotone"
              dataKey="displayValue"
              name="MtM"
              stroke={COLORS.orange500}
              strokeWidth={2}
              dot={{ fill: COLORS.orange500, strokeWidth: 0 }}
              activeDot={{ fill: COLORS.orange700, strokeWidth: 0 }}
              hide={!hasNonZeroValues}
              isAnimationActive={shouldAnimate}
              animationDuration={500}
            >
              <LabelList
                dataKey="value"
                position="top"
                offset={12}
                fontSize=".625rem"
                formatter={(value) => (shouldRenderValueLabel(Number(value)) ? formatMillionCurrencyLabel(Number(value)) : '')}
                style={{ fill: COLORS.gray500 }}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </Flex>
    </Flex>
  );
}

export default CounterpartyMtmChart;
